import {
  builder,
  defaults,
  displayTrackersConfig
} from '../../../common/js/config/index';
import { units } from '../../../common/js/config/constants';
import { commonVideoConfig } from './config-common-video';
import { EndScreenType } from '../lib/end-screen/end-screen-types';

export const unmarshaller = {
  isCreativeTransparent: builder.boolean('is_creative_transparent', {
    defaultValue: false
  }),
  watermarkApiKeys: defaults.watermarkApiKeys,
  omidTrackingEnabled: defaults.omidTrackingEnabled,
  formatBaseUrl: builder.string('format_base_url', { optional: true }),
  clickUrl: builder.string('click_url', { optional: true }),
  displayTrackers: displayTrackersConfig(),
  omidTrackers: defaults.omidTrackers,
  alternativeAutoplayDetect: builder.boolean('alternative_autoplay_detect', {
    defaultValue: false
  }),
  alternativeAutoplayLog: builder.boolean('alternative_autoplay_log', {
    defaultValue: false
  }),
  ignoreClicksBefore: builder.number('ignore_clicks_before', {
    defaultValue: 1000,
    description: 'Value in milliseconds',
    unit: units.milliseconds
  }),
  locale: defaults.locale,
  browser: builder.holder({
    type: builder.string('browser', {
      possibleValues: [
        {
          name: 'Ogury browser',
          value: 'browser',
          comment: 'Internal delivery only'
        },
        {
          name: 'Default browser',
          value: 'defaultbrowser'
        }
      ],
      defaultValue: 'defaultbrowser',
      label: 'Browser',
      description:
        'Browser used to open the redirection url. Ogury Browser is only available for in-app ad formats.'
    }),
    preload: builder.boolean('browser_enable_preload', {
      defaultValue: true
    })
  }),
  interaction: builder.string('interaction', {
    of: ['touch', 'click'],
    defaultValue: 'click'
  }),
  orientation: builder.string('orientation', {
    of: ['auto', 'landscape', 'portrait'],
    possibleValues: [
      {
        name: 'Auto',
        value: 'auto'
      },
      {
        name: 'Landscape',
        value: 'landscape'
      },
      {
        name: 'Portrait',
        value: 'portrait'
      }
    ],
    defaultValue: 'auto',
    label: 'Orientation',
    description: 'Specify device orientation to display the format'
  }),
  debug: builder.boolean('_debug', {
    defaultValue: false
  }),
  isProgrammatic: builder.boolean('is_programmatic', {
    label: 'Programmatic',
    description: 'Define if the creative is managed or programmatic',
    defaultValue: false
  }),
  video: builder.holder({
    creative: builder.string('video_creative_type', {
      of: ['vast', 'mp4'],
      defaultValue: 'vast',
      possibleValues: [
        {
          name: 'Vast URL',
          value: 'vast'
        },
        {
          name: 'MP4 Video',
          value: 'mp4'
        }
      ],
      label: 'Video creative'
    }),
    creativeRatio: builder.number('creative_ratio', {
      label: 'Creative Ratio',
      description: `Ratio of the Asset<br>
  Define if the creative orientation is portrait or landscape<br>
  Mandatory for Thumbnail Ad delivery<br>
  Default values are 1.78 for landscape and 0.56 for portrait for a 16:9 Asset<br>
  Automatically computed from uploaded Assets<br>
  Need to be manually defined for video programmatic, if not available in the video VAST or Adserver Redirect tags`,
      if: {
        valueOf: 'is_programmatic',
        is: false
      },
      unit: units.aspectRatio
    }),
    videoDuration: builder.number('video_duration', {
      label: 'Video duration',
      description: 'Duration of the video',
      if: {
        valueOf: 'is_programmatic',
        is: false
      },
      unit: units.seconds
    }),
    sendTracksOnClick: builder.boolean('all_quartiles_on_click', {
      defaultValue: true
    }),
    billedAfter: builder.number('completed_video_duration', {
      label: 'Completed video duration',
      description: 'Time in seconds to consider the video completed',
      allOf: [
        {
          valueOf: 'is_programmatic',
          is: false
        }
      ],
      unit: units.seconds
    }),
    soundOnStart: builder.boolean('sound_on_video_start', {
      defaultValue: false,
      label: 'Sound at startup',
      description: 'Set to Yes if the sound is played at video startup'
    }),
    vast: builder.string('vast', {
      allOf: [
        {
          valueOf: 'video_creative_type',
          is: 'vast'
        },
        {
          valueOf: 'is_programmatic',
          is: false
        }
      ],
      pattern: '^$|^https://.*$',
      message: 'is not a valid VAST URL'
    }),
    vastContent: builder.string('vast_content', {
      optional: true
    }),
    vastVideoSource: builder.string('vast_video_source', {
      if: {
        valueOf: 'video_creative_type',
        is: 'mp4'
      }
    }),
    cachedVideo: builder.string('vast_video_offline_url', {
      if: {
        valueOf: 'video_creative_type',
        is: 'mp4'
      }
    }),
    enableBase64Download: builder.boolean('enable_base64_download', {
      defaultValue: false
    }),
    forceDisableBase64Download: builder.boolean(
      'force_disable_base64_download',
      {
        defaultValue: false
      }
    ),
    enableLinkPreload: builder.boolean('enable_link_preload', {
      defaultValue: false
    }),
    thumbnail: builder.holder({
      previewUrl: builder.string('thumbnail_preview_url', {
        optional: true
      }),
      percentX: builder.number('thumbnail_percent_x', {
        defaultValue: 0
      }),
      percentY: builder.number('thumbnail_percent_y', {
        defaultValue: 0
      }),
      percentWidth: builder.number('thumbnail_percent_width', {
        defaultValue: 1
      }),
      percentHeight: builder.number('thumbnail_percent_height', {
        defaultValue: 1
      }),
      time: builder.number('thumbnail_time', {
        defaultValue: 1,
        unit: units.seconds
      })
    }),
    enableOpacityOnShow: builder.boolean('enable_video_opacity_on_show', {
      defaultValue: true
    }),
    debugVastContent: builder.boolean('debug_vast_content', {
      defaultValue: false
    }),
    ...commonVideoConfig
  }),
  icon: builder.holder({
    useIcon: builder.boolean('use_icon', {
      defaultValue: false,
      label: 'Icon',
      description:
        'Creates an icon on the user homescreen opening the browser with the landing page. Only works for Android < 8.0.0'
    }),
    url: builder.icon('icon', {
      overrideType: 'image',
      label: 'Icon file',
      description:
        'Upload a PNG or JPG, min 256*256px. We recommend a square picture, otherwise, it will be cropped.',
      if: 'use_icon'
    }),
    name: builder.string('icon_name', {
      label: 'Icon name',
      description: 'Application name',
      if: 'use_icon'
    }),
    size: builder.number('icon_size', {
      defaultValue: 75,
      unit: units.pixel
    }),
    uppercase: builder.boolean('icon_uppercase', {
      defaultValue: true
    }),
    identifier: builder.string('identifier', {
      overrideType: 'hidden',
      label: 'Icon identifier',
      description:
        'If two campaigns have the same identifier, when the user install the icon for the second time it will be replaced instead of duplicated.',
      if: 'use_icon',
      optional: true
    }),
    isAllowedAfterBrowser: builder.boolean('allow_icon_after_browser', {
      defaultValue: true
    })
  }),

  global: builder.holder({
    sdkVersion: defaults.sdkVersion,
    odid: defaults.odid,
    application: defaults.application
  }),
  /**
   * Analytics
   */
  analytics: builder.holder({
    enable: builder.boolean('analytics_enable', {
      label: 'Enable analytics',
      description: 'Allow sending of analytics tracks',
      defaultValue: false
    })
  }),
  enableRealQuartiles: builder.boolean('enable_real_quartiles', {
    description: 'Allow sending real quartiles',
    defaultValue: false
  }),

  omidVideoTrackers: builder.string('omid_video_trackers', {
    defaultValue: '',
    description: 'OMID Video trackers'
  }),

  // New params for Video MPU (300x250)
  colors: builder.holder({
    primaryColor: builder.color('primary_color', {
      defaultValue: '#4185F4',
      label: 'Primary color',
      description: 'Used for brand website / CTA bar label'
    }),
    secondaryColor: builder.color('secondary_color', {
      defaultValue: '#FFFFFF',
      label: 'Secondary color',
      description: 'Used for CTA bar animation color'
    })
  }),

  brandItem: builder.holder({
    name: builder.string('brand_name', {
      label: 'Brand name',
      description:
        'Name of the brand, can be displayed over the creative and in a creative wording with #{brand} macro'
    }),
    domain: builder.string('brand_domain', {
      label: 'Brand domain',
      description: 'Website domain of the brand, used in the logo part',
      optional: true
    }),
    logo: builder.string('brand_logo', {
      label: 'Brand logo',
      overrideType: 'image',
      description:
        'Logo of the brand, displayed over the creative. Recommended size 34x34px',
      optional: true
    })
  }),

  cta: builder.holder({
    label: builder.string('cta_label', {
      defaultValue: 'Learn more',
      label: 'Call to action label',
      description: 'Text visible on the call to action'
    }),
    animationDelay: builder.number('cta_animation_delay', {
      defaultValue: 3,
      label: 'CTA animation delay',
      description: 'Delay before changing background color',
      unit: units.seconds
    })
  }),

  controls: builder.holder({
    sound: builder.holder({
      enabled: builder.boolean('display_sound_button', {
        defaultValue: true,
        label: 'Display sound button',
        description: 'Show or hide the sound button'
      })
    }),
    backgroundColor: builder.color('controls_background_color', {
      defaultValue: '#000000',
      label: 'Background color',
      description: 'Background color of the controls'
    }),
    backgroundOpacity: builder.number('controls_background_opacity', {
      defaultValue: 30,
      label: 'Background opacity',
      description: 'Background opacity (%) of the controls',
      unit: units.percentage
    }),
    labelColor: builder.color('controls_label_color', {
      defaultValue: '#FFFFFF',
      label: 'Label color',
      description: 'Color of the controllers labels'
    }),
    labelOpacity: builder.number('controls_label_opacity', {
      defaultValue: 100,
      label: 'Label opacity',
      description: 'Opacity (%) of the controllers labels',
      unit: units.percentage
    }),
    showProgressBar: builder.boolean('show_progress_bar', {
      defaultValue: true
    })
  }),
  skin: builder.holder({
    endScreen: builder.string('end_screen_image', {
      optional: true,
      overrideType: 'image',
      label: 'End screen file',
      description:
        'To be visible "auto close delay" should be greater than 0. Will be displayed on the last frame of the video. Make sure that the picture respects the ratio of the video. Recommended size is 1280px * 720px.'
    })
  }),

  skipAfterDelay: builder.holder({
    enabled: builder.boolean(`skip_or_continue_after_delay_display`, {
      defaultValue: false,
      label: 'After delay layer',
      description: 'Display a layer after 15s of video viewed'
    })
  }),

  displayVastIcon: builder.boolean('display_vast_icon', {
    defaultValue: false,
    description: 'Display vast icon (adchoices)'
  }),
  hasVpaidTracking: builder.boolean('vpaid_tracking', {
    defaultValue: false
  }),
  optOut: builder.holder({
    hide: builder.boolean('hide_opt_out', {
      defaultValue: false,
      description: 'Hide opt out ONLY IF display vast icon (adchoices)'
    }),
    closeAdOnClick: builder.boolean('close_ad_on_optout_click', {
      defaultValue: false,
      description:
        'Close the ad when the user clicks on the opt-out link. If false, the ad will not be closed.'
    })
  }),
  endScreen: builder.holder({
    allowListType: builder.arrayString('end_screen_allow_list_type', {
      defaultValue: [
        EndScreenType.DEFAULT,
        EndScreenType.APP_PREVIEW,
        EndScreenType.IMAGE,
        EndScreenType.HTML,
        EndScreenType.IFRAME
      ]
    }),
    forced: builder.boolean('force_vast_end_screen', {
      defaultValue: false
    }),
    duration: builder.number('end_screen_duration', {
      defaultValue: 120,
      label: 'End screen duration',
      description:
        'Number of seconds before closing the format after the video reaches its end when having an end screen content given by the vast',
      unit: units.seconds
    }),
    closeBtn: builder.holder({
      delay: builder.number('end_screen_close_btn_delay', {
        defaultValue: 0,
        unit: units.seconds
      }),
      size: builder.number('end_screen_close_btn_size', {
        defaultValue: 100,
        unit: units.pixel
      })
    })
  }),
  isMraidCompliant: builder.boolean('is_mraid_compliant', {
    label: 'Is Mraid Compliant',
    description:
      'Determines if the format is Mraid compliant. If not, the format will need to polyfill the Mraid API',
    defaultValue: true
  }),
  isOgurySdk: builder.boolean('is_ogury_sdk', {
    label: 'Is Ogury SDK',
    description: 'Determines if the format is delivered through Ogury SDK',
    defaultValue: true
  })
};
